import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Server, CheckCircle, AlertTriangle } from 'lucide-react';
import type { ContainerAPI } from '@sbclite/core';
import { api } from '@/services/api';
import { summarizeEngineHealth } from '@/lib/dashboard-health';
import { ErrorState, LoadingState, EmptyState, StatsGrid, StatCard, StatusBadge } from '@/components/common';
import { useAuth } from '@/contexts/AuthContext';

export default function SystemHealthDashboard() {
  const { user } = useAuth();
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const query = useQuery({
    queryKey: ['dashboard', 'engine-observations'],
    queryFn: async () => (await api.get<ContainerAPI.ListResponse>('/api/containers/list')).data,
    refetchInterval: 30000,
  });
  const summary = query.data ? summarizeEngineHealth(query.data.containers, now) : undefined;
  return (
    <section className="bg-white rounded-lg border p-6 space-y-4">
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Engine observations</h2>
          <p className="text-sm text-gray-600">Registry lifecycle and heartbeat observations. These do not confirm call or media readiness.</p>
        </div>
        <Link className="text-sm font-medium text-blue-700 hover:text-blue-900" to="/system/containers">Manage engines →</Link>
      </div>
      {query.isLoading ? <LoadingState /> : query.isError ? (
        <ErrorState title="Unable to read engine observations" message={query.error.message} onRetry={() => void query.refetch()} />
      ) : summary ? <>
        <StatusBadge status={summary.status === 'critical' ? 'unhealthy' : summary.status === 'offline' ? 'disabled' : summary.status} label={`Registry observations: ${summary.status}`} />
        {summary.status !== 'healthy' && summary.engines.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 flex gap-3">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm">{summary.engines.length - summary.healthy} engine(s) have stale heartbeats or do not report healthy status. <Link className="font-medium underline" to="/system/containers">Review engine status and prerequisites</Link>.</p>
          </div>
        )}
        <StatsGrid columns={3}>
          <StatCard label="Registered engines" value={summary.engines.length} icon={Server} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
          <StatCard label="Fresh and reporting healthy" value={summary.healthy} icon={CheckCircle} iconBgColor="bg-green-100" iconColor="text-green-600" />
          <StatCard label="Need review" value={summary.engines.length - summary.healthy} icon={AlertTriangle} iconBgColor="bg-amber-100" iconColor="text-amber-600" />
        </StatsGrid>
        {summary.engines.length === 0 ? <EmptyState title="No SIP engines registered" description="Deploy the SIP engine externally; it will appear automatically under SIP Engines." icon={Server} /> : (
          <div className="overflow-x-auto rounded-lg border">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50"><tr>
                {['Engine', 'Reported lifecycle', 'Heartbeat', 'Observation'].map(label => <th key={label} scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">{label}</th>)}
              </tr></thead>
              <tbody className="divide-y divide-gray-200">
                {summary.engines.map(engine => <tr key={engine.containerId} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm"><Link to={`/system/containers/${encodeURIComponent(engine.containerId)}`} className="font-medium text-blue-700 break-all">{engine.containerId}</Link><div className="text-xs text-gray-500">{engine.ipAddress} · {engine.region}</div></td>
                  <td className="px-6 py-4 text-sm text-gray-600">{engine.status}</td>
                  <td className="px-6 py-4 text-sm"><div>{engine.fresh ? 'Fresh' : 'Stale or unknown'}</div><div className="text-xs text-gray-500">{Number.isFinite(Date.parse(engine.lastHeartbeat)) ? new Date(engine.lastHeartbeat).toLocaleString() : 'Unknown'}</div></td>
                  <td className="px-6 py-4"><StatusBadge status={engine.observedHealthy ? 'healthy' : 'warning'} label={engine.observedHealthy ? 'Fresh / healthy' : 'Review required'} /></td>
                </tr>)}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-xs text-gray-500">Fresh means a heartbeat less than two minutes old. Registry fetched {new Date(query.dataUpdatedAt).toLocaleString()}.</p>
        <div className="border-t pt-4 flex flex-wrap justify-between gap-3 text-sm text-gray-600">
          <p>Live call count and effective capacity are not reported by this registry.</p>
          {user?.role === 'admin' && <Link className="font-medium text-blue-700 hover:text-blue-900" to="/system/ongoing-calls">View active calls →</Link>}
          {summary.engines.length === 0 && <Link className="font-medium text-blue-700" to="/system/containers">Open SIP Engines →</Link>}
        </div>
      </> : null}
    </section>
  );
}
